<script setup>
import { ref, computed, onMounted } from 'vue'
import { Dialog } from 'quasar'
import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { usePlanningStore } from '@/stores/planning.store'
import { ABSENCE_ICON, useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore, staffDetailAllocationsFilterKey } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { calendarPresets, clampToPresets, rangeForPreset } from '@/composables/date-range-presets'
import { useStaffMovements } from '@/composables/staff-movements'
import { useHighlightNewRow } from '@/composables/scroll'
import { sortByFields } from '@/composables/filter-sort'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'
import StaffMovementListItem from '@/components/planner/StaffMovementListItem.vue'
import VacationEditor from '@/components/shared/VacationEditor.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'

const props = defineProps({
  staff: { type: Object, required: true },
  tab: { type: String, default: null },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const { notifySuccess } = useNotify()
const { groupAllocations, noStockDialog, approveAllocationAsync, revertAllocationAsync } = useStaffMovements()

const activeTab = ref('holidays')
const currentTab = computed(() => props.tab ?? activeTab.value)
const holidays = ref([])
const allocations = ref([])
const editVacation = ref(null)
const holidaysEditMode = ref(false)
const holidaysListRef = ref(null)
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const filterSort = computed(() => webSiteStore.filterSortFields[staffDetailAllocationsFilterKey])

const defaultPreset = clampToPresets(
  webSiteStore.getDefaultTimelinePeriod(),
  calendarPresets,
  portalStore.dateRangePresetEnum.MONTH,
)
const initialRange = rangeForPreset(defaultPreset)
const range = ref({ from: initialRange.from.format('YYYY-MM-DD'), to: initialRange.to.format('YYYY-MM-DD') })

const toRows = (res) => res?.data?.results ?? res?.data ?? []
const rangeParams = () => ({
  start: dayjs(range.value.from).startOf('day').toISOString(),
  end: dayjs(range.value.to).endOf('day').toISOString(),
})

const fetchHolidaysAsync = async () => {
  const res = await scheduleStore.fetchEventsAsync({ vacation: true, service_ids: String(props.staff.id), all: true, ...rangeParams() })
  holidays.value = toRows(res)
}

const fetchAllocationsAsync = async () => {
  const res = await scheduleStore.fetchEventsAsync({ allocations: true, service_ids: String(props.staff.id), ...rangeParams() })
  allocations.value = toRows(res)
}

const refreshAsync = async () => {
  if (props.tab === 'holidays') return fetchHolidaysAsync()
  if (props.tab === 'allocation') return fetchAllocationsAsync()
  await Promise.all([fetchHolidaysAsync(), fetchAllocationsAsync()])
}

onMounted(async () => {
  if (!planningStore.projects?.length) await planningStore.fetchProjectsAsync({ all: true }, true)
  await refreshAsync()
})

const onRangeChange = async (value) => {
  range.value = value
  await refreshAsync()
}

const sortValue = (row, field) => {
  if (field === 'start') return row.start ?? null
  return row.name?.toLowerCase() ?? null
}

const allocationRows = computed(() => groupAllocations(allocations.value))

const filteredAllocationRows = computed(() => {
  const query = filterSort.value?.filterValue?.trim().toLowerCase()
  const rows = query
    ? allocationRows.value.filter(group => group.name.toLowerCase().includes(query))
    : allocationRows.value
  return sortByFields(rows, filterSort.value?.sortFields, sortValue)
})

const onAddHoliday = () => {
  editVacation.value = {
    service: props.staff.id,
    start: dayjs().startOf('day').toISOString(),
    end: dayjs().endOf('day').toISOString(),
  }
}

const onVacationUpdate = async () => {
  const isCreate = !editVacation.value?.id
  editVacation.value = null
  const existingIds = new Set(holidays.value.map(event => event.id))
  await fetchHolidaysAsync()
  if (!isCreate) return
  const newHoliday = holidays.value.find(event => !existingIds.has(event.id))
  await highlightRowAsync(newHoliday?.id, holidaysListRef)
}

const onApprove = async (event) => {
  await scheduleStore.upsertEventAsync({ id: event.id, confirmed: true })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await fetchHolidaysAsync()
}

const onRevert = async (event) => {
  await scheduleStore.upsertEventAsync({ id: event.id, confirmed: false })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await fetchHolidaysAsync()
}

const onApproveAllocation = (group) => approveAllocationAsync(group, fetchAllocationsAsync)

const onRevertAllocation = (group) => revertAllocationAsync(group, fetchAllocationsAsync)

const onDelete = (event) => {
  Dialog.create({ message: i18n.t('common.confirmDelete'), cancel: true, persistent: true }).onOk(async () => {
    await scheduleStore.deleteEventAsync(event.id)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    await fetchHolidaysAsync()
  })
}
</script>
<template>
  <div>
    <no-stock-confirm-dialog v-model="noStockDialog" />
    <q-dialog
      :maximized="isMobile"
      :model-value="editVacation !== null"
      class="dialog-xs"
      @hide="editVacation = null"
    >
      <vacation-editor
        v-if="editVacation !== null"
        :model-value="editVacation"
        @update="onVacationUpdate"
        @cancel="editVacation = null"
      />
    </q-dialog>

    <filter-sort-selector
      v-if="currentTab === 'allocation'"
      hide-archived
      :filter-key="staffDetailAllocationsFilterKey"
    />

    <div class="q-px-sm q-py-xs">
      <date-range-navigator
        :model-value="range"
        :default-preset="defaultPreset"
        :allowed-presets="calendarPresets"
        compact
        @update:model-value="onRangeChange"
      />
    </div>

    <template v-if="!props.tab">
      <q-tabs
        v-model="activeTab"
        align="justify"
        active-color="primary"
        indicator-color="primary"
      >
        <q-tab name="holidays" :label="i18n.t('common.staff.holidays')" />
        <q-tab name="allocation" :label="i18n.t('common.staff.allocation')" />
      </q-tabs>
      <q-separator />
    </template>

    <q-tab-panels :model-value="currentTab" animated>
      <q-tab-panel name="holidays" class="q-pa-none">
        <div class="row justify-end items-center q-px-md q-pt-sm">
          <q-btn
            v-if="holidays.length"
            class="q-mr-sm"
            flat
            round
            dense
            :icon="holidaysEditMode ? 'mdi-pencil-off' : 'mdi-pencil'"
            :color="holidaysEditMode ? 'primary' : 'grey-6'"
            @click="holidaysEditMode = !holidaysEditMode"
          >
            <q-tooltip>{{ holidaysEditMode ? i18n.t('common.done') : i18n.t('common.edit') }}</q-tooltip>
          </q-btn>
          <q-btn
            style="padding: 0;"
            size="md"
            unelevated
            ripple
            rounded
            icon="mdi-plus-circle-outline"
            @click="onAddHoliday"
          />
        </div>
        <q-list v-if="holidays.length" ref="holidaysListRef">
          <staff-movement-list-item
            v-for="event in holidays"
            :key="event.id"
            :class="{ 'row-highlight': event.id === highlightedRowId }"
            :event="event"
            :label="i18n.t('common.vacation')"
            :avatar-icon="ABSENCE_ICON"
            show-status
            :can-approve="webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS)"
            :edit-mode="holidaysEditMode"
            @approve="onApprove"
            @revert="onRevert"
            @edit="editVacation = { ...$event }"
            @delete="onDelete"
          />
        </q-list>
        <div v-else class="text-center text-grey-6 q-pa-lg">
          {{ i18n.t('common.noData') }}
        </div>
      </q-tab-panel>

      <q-tab-panel name="allocation" class="q-pa-none">
        <q-list v-if="filteredAllocationRows.length">
          <staff-movement-list-item
            v-for="group in filteredAllocationRows"
            :key="group.key"
            :event="group"
            :label="group.name"
            :color="group.color"
            avatar-icon="mdi-briefcase-outline"
            show-status
            :can-approve="webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS)"
            @approve="onApproveAllocation"
            @revert="onRevertAllocation"
          />
        </q-list>
        <div v-else class="text-center text-grey-6 q-pa-lg">
          {{ i18n.t('common.noData') }}
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </div>
</template>
